<script lang="ts" setup>
import { URMRole, URMRoleKey, URMRoles } from "@/models/dto/userRoleManagement";
import { computed, ref, watch } from "vue";
import { ContactGroup, Contact } from "@/models/dto/ContactGroup";
import DialogLayout from "@/components/common/DialogLayout.vue";
import CreateEditContactContent from "./CreateEditContactContent.vue";
import { ContactType } from "./utils/helpers";

const emit = defineEmits([
  "input",
  "close",
  "click:header-action-right",
  "click:header-action-left",
  "click:edit-contact",
  "click:remove-contact",
  "remove:contact",
  "update:contact",
  "add:contact",
]);
const defaultUserRoles = [URMRoles.TripAuthorizedUser, URMRoles.TripViewOnly];

const authorizedUserDisabledRoles = [URMRoles.TripAuthorizedUser];

const props = withDefaults(
  defineProps<{
    value: boolean;
    title: string;
    subtitle?: string;
    submitButtonText: string;
    headerActionRightText?: string;
    headerActionLeftText?: string;
    userRoles?: URMRole[];
    contactGroups: ContactGroup[];
    defaultDisabledUserRoles?: URMRole[];
    userTypeDisabled?: boolean;
    userTypeMessage?: string;
    defaultUserType?: URMRoleKey;
    persistent?: boolean;
    contact?: Contact;
    isEditMode: boolean;
    preventDefaultHeaderClose?: boolean;
    contactListIsEditable?: boolean;
    contactType: ContactType;
    entityId: number;
  }>(),
  {
    value: false,
    preventDefaultHeaderClose: false,
    contactListIsEditable: false,
  }
);

const isDialogOpen = ref<boolean>(false);
const defaultDisabledUserRoles = computed(() => {
  return props.defaultDisabledUserRoles || authorizedUserDisabledRoles;
});
const userRoles = ref<URMRole[]>(props.userRoles || defaultUserRoles);
const transitionName = ref("slide-right");
const shouldAnimate = ref(false);

let prevIsEditMode = props.isEditMode;
let prevContactId = props.contact?.externalId;

watch(
  () => props.value,
  (newValue) => {
    isDialogOpen.value = newValue;
    if (!newValue) {
      shouldAnimate.value = false;
    }
  }
);

// this controls the sliding animation between the edit and create contact modes
// we want to animate when the dialog is open and the edit mode changes
// the content will slide left when switching to edit mode and right when switching to create mode
watch(
  [() => props.isEditMode, () => props.contact?.externalId],
  ([newIsEditMode, newContactId], [oldIsEditMode, oldContactId]) => {
    if (isDialogOpen.value) {
      if (oldIsEditMode !== undefined && oldIsEditMode !== newIsEditMode) {
        shouldAnimate.value = true;
        transitionName.value = newIsEditMode ? "slide-left" : "slide-right";
      }
    }
    prevIsEditMode = newIsEditMode;
    prevContactId = newContactId;
  },
  { immediate: true }
);

watch(
  () => isDialogOpen.value,
  (newValue) => {
    if (newValue !== props.value) {
      emit("input", newValue);
    }
  }
);

const handleClose = () => {
  isDialogOpen.value = false;
  shouldAnimate.value = false;
  transitionName.value = "slide-right";
  emit("input", false);
};

const handleDialogInput = (val) => {
  isDialogOpen.value = val;
  if (!val) {
    handleClose();
  }
};

const handleEditContactClick = (payload: Contact) => {
  emit("click:edit-contact", payload);
};

const handleRemoveContactClick = (payload: Contact) => {
  emit("click:remove-contact", payload);
};

</script>

<template>
  <DialogLayout
    :value="isDialogOpen"
    @input="handleDialogInput"
    :persistent="persistent"
    :header-action-right-text="headerActionRightText"
    :header-action-left-text="headerActionLeftText"
    :prevent-default-header-close="preventDefaultHeaderClose"
    @click:header-action-right="emit('click:header-action-right')"
    @click:header-action-left="emit('click:header-action-left')"
  >
    <template #activator="slotProps">
      <slot name="activator" v-bind="slotProps" />
    </template>

    <template #contentLayout>
      <transition :name="shouldAnimate ? transitionName : ''" mode="out-in">
      <CreateEditContactContent
        :title="title"
        :subtitle="subtitle"
        :contact-groups="contactGroups"
        :is-edit-mode="isEditMode"
        :contact="contact"
        :user-roles="userRoles"
        :default-disabled-user-roles="defaultDisabledUserRoles"
        :user-type-message="userTypeMessage"
        :default-user-type="defaultUserType"
        :submit-button-text="submitButtonText"
        :contact-list-is-editable="contactListIsEditable"
        @remove:contact="emit('remove:contact')"
        @add:contact="emit('add:contact')"
        @update:contact="emit('update:contact')"
        @input="handleDialogInput"
        @click:edit-contact="handleEditContactClick"
        @click:remove-contact="handleRemoveContactClick"
        :contact-type="contactType"
        :entity-id="entityId"
        :key="contact?.externalId + '-' + isEditMode"
      />
    </transition>
    </template>
  </DialogLayout>
</template>

<style lang="scss" scoped>.slide-left-enter-active, .slide-left-leave-active {
  transition: all 0.4s cubic-bezier(.55,0,.1,1);
}
.slide-left-enter-from {
  opacity: 0;
  transform: translateX(40px);
}
.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-40px);
}
.slide-left-enter-to, .slide-left-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.slide-right-enter-active, .slide-right-leave-active {
  transition: all 0.4s cubic-bezier(.55,0,.1,1);
}
.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-40px);
}
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
.slide-right-enter-to, .slide-right-leave-from {
  opacity: 1;
  transform: translateX(0);
}</style>
